/**
 * Roles & Permissions contract.
 *
 * Provides endpoints to list, retrieve, create, update, and delete roles and role-permission mappings.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, orgIdSchema, uuidSchema } from '../schemas/common.js';
import { SCOPES } from '../permissions/roles.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const roleSummarySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  label: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  maxScope: z.enum(SCOPES),
  permissionsCount: z.number().int(),
  /** People holding the role, by hand or from the org chart (ADR 0024), counted once each. */
  assignedUsersCount: z.number().int(),
  /**
   * The org chart grants this role automatically (`manager`, `department_head`). Optional only so
   * the contract can ship ahead of the API that fills it (invariant 8); absent means false.
   */
  grantedFromOrgChart: z.boolean().optional(),
  /**
   * An administrator may grant it by hand. False for `department_head`: set the department's head.
   * Absent means true.
   */
  handAssignable: z.boolean().optional(),
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
});
export type RoleSummary = z.infer<typeof roleSummarySchema>;

export const roleDetailSchema = roleSummarySchema.extend({
  permissions: z.array(z.string()),
});
export type RoleDetail = z.infer<typeof roleDetailSchema>;

export const createRoleSchema = z.object({
  name: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9_]+$/, 'Name must be lowercase alphanumeric with underscores'),
  label: z.string().min(2).max(128),
  description: z.string().max(500).optional(),
  maxScope: z.enum(SCOPES).default('org'),
  permissions: z.array(z.string()),
});
export type CreateRoleInput = z.infer<typeof createRoleSchema>;

export const updateRoleSchema = z.object({
  label: z.string().min(2).max(128).optional(),
  description: z.string().max(500).optional(),
  maxScope: z.enum(SCOPES).optional(),
  permissions: z.array(z.string()).optional(),
});
export type UpdateRoleInput = z.infer<typeof updateRoleSchema>;

export const permissionCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  permissions: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      description: z.string(),
    }),
  ),
});
export type PermissionCategory = z.infer<typeof permissionCategorySchema>;

/**
 * Where an assignment comes from (ADR 0024). `assigned` is a `role_grants` row an administrator
 * made, revocable by hand. `org_chart` is derived from the org chart when the person signs in —
 * reporting to them makes someone a Reporting Manager, heading a department a Department Head — and
 * is not revocable here: change the reporting line or the department's head instead.
 */
export const ROLE_ASSIGNMENT_SOURCES = ['assigned', 'org_chart'] as const;
export type RoleAssignmentSource = (typeof ROLE_ASSIGNMENT_SOURCES)[number];

export const roleAssignmentSchema = z.object({
  /**
   * For an `assigned` row, the grant id to revoke. For an `org_chart` row, a stable id of what
   * grants it — the department for a Department Head, the person for a Reporting Manager — which no
   * revoke will match.
   */
  id: uuidSchema,
  /** Absent means `assigned` (optional so the contract can ship ahead of the API, invariant 8). */
  source: z.enum(ROLE_ASSIGNMENT_SOURCES).optional(),
  personId: uuidSchema,
  personName: z.string(),
  /** Null for a phone-only employee (no work email). */
  personEmail: z.string().nullable(),
  scope: z.enum(SCOPES),
  scopeId: uuidSchema.nullable(),
  /**
   * When the hand grant took effect. Null for an `org_chart` row: the grant holds for as long as
   * the position does, and the org chart does not record one start date for "has reports".
   */
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
});
export type RoleAssignment = z.infer<typeof roleAssignmentSchema>;

export const assignRoleSchema = z.object({
  personId: uuidSchema,
  scope: z.enum(SCOPES),
  scopeId: uuidSchema.optional(),
});
export type AssignRoleInput = z.infer<typeof assignRoleSchema>;

export const rolesStatsSchema = z.object({
  totalRoles: z.number().int(),
  systemRoles: z.number().int(),
  customRoles: z.number().int(),
  totalAssignments: z.number().int(),
});
export type RolesStats = z.infer<typeof rolesStatsSchema>;

export const listRolesResponseSchema = z.object({
  roles: z.array(roleSummarySchema),
  stats: rolesStatsSchema,
});
export type ListRolesResponse = z.infer<typeof listRolesResponseSchema>;

export const rolesContract = c.router({
  listRoles: {
    method: 'GET',
    path: '/roles',
    responses: {
      200: listRolesResponseSchema,
      401: errorBodySchema,
      403: errorBodySchema,
    },
    summary: 'List system and custom roles with stats',
    metadata: routeMeta(['org.settings:read', 'role:read']),
  },
  getRole: {
    method: 'GET',
    path: '/roles/:name',
    pathParams: z.object({ name: z.string() }),
    responses: {
      200: roleDetailSchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    summary: 'Get role details and permission list by role key',
    metadata: routeMeta(['org.settings:read', 'role:read']),
  },
  createRole: {
    method: 'POST',
    path: '/roles',
    body: createRoleSchema,
    responses: {
      201: roleDetailSchema,
      400: errorBodySchema,
      401: errorBodySchema,
      403: errorBodySchema,
      409: errorBodySchema,
    },
    summary: 'Create a new custom role with initial permissions',
    metadata: routeMeta(['org.settings:update', 'role:assign']),
  },
  updateRole: {
    method: 'PUT',
    path: '/roles/:name',
    pathParams: z.object({ name: z.string() }),
    body: updateRoleSchema,
    responses: {
      200: roleDetailSchema,
      400: errorBodySchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    summary: 'Update role details or permission assignments',
    metadata: routeMeta(['org.settings:update', 'role:assign']),
  },
  deleteRole: {
    method: 'DELETE',
    path: '/roles/:name',
    pathParams: z.object({ name: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({ success: z.boolean(), message: z.string() }),
      400: errorBodySchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      409: errorBodySchema,
    },
    summary: 'Delete custom role',
    metadata: routeMeta(['org.settings:update', 'role:assign']),
  },
  getPermissionCatalogue: {
    method: 'GET',
    path: '/roles/permissions/catalogue',
    responses: {
      200: z.array(permissionCategorySchema),
      401: errorBodySchema,
    },
    summary: 'Get grouped permission catalogue for permission matrix rendering',
    metadata: routeMeta(null),
  },
  listRoleAssignments: {
    method: 'GET',
    path: '/roles/:name/assignments',
    pathParams: z.object({ name: z.string() }),
    responses: {
      200: z.object({ assignments: z.array(roleAssignmentSchema) }),
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    summary:
      'List people currently holding this role, with their scope: hand grants and those derived from the org chart',
    metadata: routeMeta(['org.settings:read', 'role:read']),
  },
  assignRole: {
    method: 'POST',
    path: '/roles/:name/assignments',
    pathParams: z.object({ name: z.string() }),
    body: assignRoleSchema,
    responses: {
      201: roleAssignmentSchema,
      400: errorBodySchema,
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
      409: errorBodySchema,
    },
    /** 400 `ROLE_NOT_HAND_ASSIGNABLE` for a role only the org chart grants (`department_head`). */
    summary: 'Grant this role to a person at a given scope',
    metadata: routeMeta(['org.settings:update', 'role:assign']),
  },
  revokeRoleAssignment: {
    method: 'DELETE',
    path: '/roles/:name/assignments/:grantId',
    pathParams: z.object({ name: z.string(), grantId: uuidSchema }),
    body: c.noBody(),
    responses: {
      200: z.object({ success: z.boolean(), message: z.string() }),
      401: errorBodySchema,
      403: errorBodySchema,
      404: errorBodySchema,
    },
    summary: 'Revoke a role grant from a person',
    metadata: routeMeta(['org.settings:update', 'role:assign']),
  },
});
